import React, { useState, useRef } from "react";
import cx from "classnames";
import { Button, Flex, Input, isNullish, Menu, Text } from "@theorgchartdev/storybook";
import { GetDoc } from "@/Utils/StateLib";
import { GetValuesForField } from "@/Utils/Promises";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { addOrRemove } from "@/Utils/Misc";
import { NumericInputField } from "@/appComponents/Blocks/Composites/ReactInputFields/NumericInputField/NumericInputField";
import DateInputField from "@/appComponents/Blocks/Composites/ReactInputFields/DateInputField/DateInputField";
import { enumFieldType } from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import {
  FormatFieldValue,
  getBlockedFieldDisplayValue,
  isBlockedField
} from "@coreapp/utils/FormatFieldValue";
import { FormatValueFromFieldName } from "@/Utils/DataLib";
import { InputValuePills } from "../InputValuePills/InputValuePills";

export const InputComponentDict = {
  [enumFieldType.number]: NumericInputField,
  [enumFieldType.date]: DateInputField
};

const GetFieldDefByName = fieldName => {
  const doc = GetDoc();
  const objDB = doc?.GetObjDB();
  const boxClass = objDB?.GetClassByName("Box");
  return boxClass?.GetFieldDefByName(fieldName);
};

export const FieldInputValuePickerComposite = ({
  field,
  value,
  onChange,
  popoverRef,
  inputProps,
  menuProps,
  triggerProps,
  showTrigger = true,
  children,
  ignorePicklistValues = false,
  showPickListWarning = false,
  dataTestIdPrefix,
  showLabel = true,
  parentFieldDef = null,
  selectedItem = null,
  mode,
  onRemoveItem,
  inputSize = "small",
  enableEnterKeyInput = false, // New prop to conditionally enable Enter key input
  queryChartId = null, // Chart ID to query field values from
  allowBlockedValueOption = false // When true, show (hidden) in dropdown (for search/filter contexts)
}) => {
  const fieldDef = parentFieldDef ? parentFieldDef : GetFieldDefByName(field);
  const className = fieldDef?.GetFieldClassName ? fieldDef?.GetFieldClassName() : "Box";
  const valueBlocked = isBlockedField(value) || value === getBlockedFieldDisplayValue();
  // Derive the input component from the field definition only. Deriving it from the
  // (transient) blocked state caused the rendered component to swap on the first keystroke
  // (text -> number/date), remounting the input and dropping focus.
  const fieldType = fieldDef?.GetFieldType() ?? enumFieldType.text;
  const fieldLabel = fieldDef?.GetFieldLabel() || fieldDef?.GetFieldName();
  const fieldName = fieldDef ? fieldDef.GetFieldName() : field;

  const [fieldValues, setFieldValues] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);

  // Create field data object for enhanced contextual information
  const fieldData = {
    field: fieldName,
    className: className,
    fieldDef: fieldDef
  };

  const GetFieldValues = async () => {
    if (!fieldName) return;
    try {
      const data = await GetValuesForField(fieldName, queryChartId);
      setFieldValues(data.results);
    } catch (error) {
      console.error(`Error loading field values for ${fieldName}:`, error);
    }
  };

  const flagValue = checkValue => {
    if (!fieldDef?.IsPickList()) return false;
    return fieldDef.GetPickValues().indexOf(checkValue) === -1 && fieldDef.IsConstrainedPickList();
  };

  const handleValuePickerClick = option => {
    if (!fieldDef?.IsList()) {
      // Pass both field name (for backward compatibility) and field data (for enhanced context)
      onChange({ field: fieldName, fieldData, value: option.value }, true);
      return;
    }

    let currentValue = value;
    if (!Array.isArray(currentValue)) {
      currentValue = !currentValue ? [] : [currentValue];
    }

    const nextValue = addOrRemove(currentValue, option.value);
    onChange({ field: fieldName, fieldData, value: nextValue }, true);
  };

  const handleKeyDown = e => {
    // Only process Enter key if the feature is enabled
    if (enableEnterKeyInput && e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();

      if (!fieldDef?.IsList()) {
        // For single value fields, replace the value
        onChange({ field: fieldName, fieldData, value: inputValue.trim() }, true);
      } else {
        // For list fields, add to the array
        let currentValue = Array.isArray(value) ? [...value] : [];
        if (value && !Array.isArray(value)) {
          currentValue = [value];
        }

        // Check if value already exists to avoid duplicates
        if (!currentValue.includes(inputValue.trim())) {
          const nextValue = [...currentValue, inputValue.trim()];
          onChange({ field: fieldName, fieldData, value: nextValue }, true);
        }
      }

      // Clear the input after adding
      setInputValue("");
    }
  };

  const handleManualInput = (e, newInputValue) => {
    // Just update the local input state when using Enter key mode
    if (enableEnterKeyInput) {
      setInputValue(newInputValue);
      return;
    }

    // Original behavior for non-Enter key mode
    if (!fieldDef?.IsList()) {
      onChange({ field: fieldName, fieldData, value: newInputValue });
      return;
    }

    // Split by comma to get individual values
    let nextValues = newInputValue
      .split(",")
      .map(v => v.trim())
      .filter(Boolean);

    // When pressing backspace on the last value, we want to clear the empty string
    if (newInputValue.charAt(newInputValue.length - 1) === "," && e.nativeEvent.data !== ",") {
      nextValues.pop();
    }

    onChange({ field: fieldName, fieldData, value: nextValues });
  };

  const handleInputBlur = e => {
    inputProps?.onBlur?.(e);

    // If using Enter key input, we just clear the input value on blur
    // without adding anything
    if (enableEnterKeyInput) {
      setInputValue("");
      return;
    }

    // Original blur handling for comma-separated values
    if (!fieldDef?.IsList()) {
      return;
    }

    // Handle list values on blur - make sure to normalize
    const blurInputValue = e.target.value;
    if (!blurInputValue.trim()) return; // Skip if empty

    let currentValue = Array.isArray(value) ? [...value] : [];
    if (value && !Array.isArray(value)) {
      currentValue = [value];
    }

    // Add new values from input
    const newValues = blurInputValue
      .split(",")
      .map(v => v.trim())
      .filter(Boolean);

    // Combine existing and new values, filtering out duplicates
    const combinedValues = [...new Set([...currentValue, ...newValues])];

    onChange({ field: fieldName, fieldData, value: combinedValues });
  };

  const GetSelectableFieldValues = () => {
    const isPicklist = fieldDef?.IsPickList();
    if (!isPicklist || ignorePicklistValues) {
      return fieldValues;
    }

    return fieldDef.GetPickValues();
  };

  const GetOptions = () => {
    let options = [];
    const values = GetSelectableFieldValues();
    for (let v of values) {
      if (!allowBlockedValueOption && v === getBlockedFieldDisplayValue()) continue;
      const highlightValue = flagValue(v);
      const isBlank = !v;
      const label = FormatFieldValue(v, fieldDef);
      let colorModifier = "default";
      let modifier = "default";
      if (highlightValue) {
        colorModifier = "warning";
        modifier = "italic";
      } else if (isBlank) {
        colorModifier = "faintgrey";
        modifier = "italic";
      }

      let valueOption = {};
      valueOption.dataTestId = `${dataTestIdPrefix}option-${v}`;
      valueOption.label = label;
      valueOption.content = (
        <Text size="xsmall" colorModifier={colorModifier} modifier={modifier}>
          {label || `(${appstr("Blank")})`}
        </Text>
      );
      valueOption.clickHandler = handleValuePickerClick;
      valueOption.value = v;
      options.push(valueOption);
    }
    return options;
  };

  const status = (() => {
    const defaultStatus = inputProps?.status ?? "default";

    const isPicklist = fieldDef?.IsPickList();
    if (!isPicklist) {
      return defaultStatus;
    }

    if (!flagValue(value) || !showPickListWarning) {
      return defaultStatus;
    }

    return "warning";
  })();

  const triggerBaseClass = "oc-value-picker";
  const { triggerModifier, ...triggerBtnProps } = triggerProps || {};
  let pickerTrigger = (
    <Button
      dataTestId={`${dataTestIdPrefix}${fieldName}-trigger`}
      iconOnly
      iconProps={{
        modifier: "white",
        name: "angle-down",
        title: "angle-down",
        size: triggerModifier === "medium" ? "medium-small" : "small"
      }}
      modifier="secondary"
      size={triggerModifier === "medium" ? "medium-small" : "small"}
      className={cx(triggerBaseClass, {
        [`${triggerBaseClass}--${triggerModifier}`]: triggerModifier
      })}
      {...triggerBtnProps}
    />
  );

  const onRemovePillItem = value => {
    if (!value) {
      // Provide both field name and field data
      onRemoveItem(field, fieldData);
    } else {
      // Provide both field name and field data when removing a specific value
      onRemoveItem({ field, fieldData, value: value });
    }
  };

  const onChangePillItem = newValues => {
    onChange({ field: fieldName, fieldData, value: newValues });
  };

  const getPillLabel = val => {
    return val === "" ? appstr("Blank") : FormatValueFromFieldName(fieldName, val);
  };

  // Determine if we should show pills
  const shouldShowPills =
    mode === "showChips" && value && (Array.isArray(value) ? value.length > 0 : Boolean(value));

  const conditionalInputProps = {
    ...inputProps,
    status,
    ...(valueBlocked ? { placeholder: getBlockedFieldDisplayValue() } : {}),
    ...(shouldShowPills
      ? {
          prefixNode: (
            <InputValuePills
              values={value}
              key={field}
              inputSize={inputSize}
              onChange={onChangePillItem}
              onRemoveItem={onRemovePillItem}
              getLabel={getPillLabel}
              inputProps={inputProps}
            />
          )
        }
      : {})
  };

  const InputComponent = InputComponentDict[fieldType] ?? Input;
  const customInputProps = !isNullish(InputComponentDict[fieldType])
    ? { customInput: Input, fieldDef: fieldDef }
    : {};

  return (
    <Flex
      gap={"8px"}
      alignItems={"flex-end"}
      alignSelf={"stretch"}
      flex={"1"}
      className={"oc-field-input-wrapper"}
    >
      <div className={"ocn-field-input-value-row"} style={{ width: "100%" }}>
        <InputComponent
          data-testid={`${dataTestIdPrefix}${field}`}
          label={showLabel ? fieldLabel : null}
          placeholder={appstr("Enter (Value)") + " " + fieldLabel}
          size="small"
          type="text"
          onChange={handleManualInput}
          onKeyDown={handleKeyDown}
          // Blocked values render empty (with a "(hidden)" placeholder) so the real-typed input
          // renders consistently and the first keystroke is captured cleanly.
          value={valueBlocked ? "" : enableEnterKeyInput ? inputValue : NormalizeFieldValue(value)}
          ref={inputRef}
          {...customInputProps}
          {...conditionalInputProps}
          onBlur={handleInputBlur}
        />
      </div>
      {children}
      {showTrigger && (
        <div style={{ marginBottom: "0", marginTop: "auto" }}>
          <Menu
            position={"bottom-end"}
            optionSize={"small"}
            listProps={FieldSelectListProps({
              customPlaceholder: appstr("Find") + " " + fieldLabel
            })}
            stayOpenOnOptionSelect={fieldDef?.IsList()}
            value={fieldDef?.IsList() ? value : undefined}
            trigger={pickerTrigger}
            onOpen={GetFieldValues}
            options={GetOptions()}
            popoverProps={{
              floatingProps: {
                ref: popoverRef
              }
            }}
            {...menuProps}
          />
        </div>
      )}
    </Flex>
  );
};

export const NormalizeFieldValue = value => {
  if (Array.isArray(value)) {
    return value.join(",");
  }

  if (isBlockedField(value)) {
    return getBlockedFieldDisplayValue();
  }

  return value || "";
};
